/**
 * Feedback / Ticket DTOs.
 *
 * - `CreateFeedbackSchema` — the public ingest body for `POST /v1/feedback`
 *   (anonymous end user, public-key + Origin guarded). Carries the captured
 *   diagnostics arrays + page metadata + transcript.
 * - `TicketSchema` — the admin-facing representation of a FeedbackSession
 *   ("ticket") returned by the dashboard API, including AI enrichment + nested
 *   attachments.
 */
import type { z } from 'zod';
import { array, boolean, coerce, email, iso, number, object, string } from 'zod';

import {
  CapturedErrorSchema,
  ConsoleLogSchema,
  PageMetadataSchema,
  TranscriptTurnSchema,
  UserActionSchema,
  ViewportSchema,
} from './capture.js';
import {
  AttachmentKindSchema,
  ChannelSchema,
  SentimentSchema,
  TicketStatusSchema,
} from './enums.js';

// ─────────────────────────────────────────────────────────────────────────────
// Ingest: POST /v1/feedback
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Public feedback-creation body. The widget posts this with its public key
 * (the route is keyed by `publicKey`, also accepted in-body for convenience).
 * Diagnostics arrays are flattened to top-level (actions/consoleLogs/errors) to
 * match the FeedbackSession columns and the plan's wording.
 */
export const CreateFeedbackSchema = object({
  /** Public key of the widget instance (also resolvable from the route). */
  publicKey: string().min(1),
  /** Channel the feedback came through. */
  channel: ChannelSchema.default('text'),
  /** The user's free-text message (optional for pure-voice/implicit). */
  message: string().max(16384).optional(),
  /** Page metadata at submit time. */
  page: PageMetadataSchema,
  /** Conversation transcript (text or voice turns). */
  transcript: array(TranscriptTurnSchema).max(500).optional(),
  // Diagnostics arrays (gated server-side by capture level).
  actions: array(UserActionSchema).max(500).optional(),
  consoleLogs: array(ConsoleLogSchema).max(500).optional(),
  errors: array(CapturedErrorSchema).max(200).optional(),
  /** Reporter email for closed-loop replies (requires opt-in). */
  endUserEmail: email().max(320).optional(),
  emailOptIn: boolean().default(false),
});
export type CreateFeedback = z.infer<typeof CreateFeedbackSchema>;

/** Response to a successful feedback submission. */
export const CreateFeedbackResponseSchema = object({
  id: string().min(1),
  status: TicketStatusSchema,
  createdAt: iso.datetime(),
});
export type CreateFeedbackResponse = z.infer<typeof CreateFeedbackResponseSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Admin: Attachment + Ticket representations
// ─────────────────────────────────────────────────────────────────────────────

/** An attachment as returned to the dashboard (with a ticketed read URL). */
export const AttachmentSchema = object({
  id: string().min(1),
  sessionId: string().min(1),
  kind: AttachmentKindSchema,
  contentType: string().min(1).max(256),
  sizeBytes: number().int().nonnegative(),
  /** Short-TTL ticketed GET url; omitted in list contexts. */
  url: string().max(4096).optional(),
  createdAt: iso.datetime(),
});
export type Attachment = z.infer<typeof AttachmentSchema>;

/** AI enrichment block attached to a ticket. */
export const AiEnrichmentSchema = object({
  summary: string().nullable().optional(),
  title: string().nullable().optional(),
  category: string().nullable().optional(),
  sentiment: SentimentSchema.nullable().optional(),
  /** 0..1 quality score. */
  qualityScore: number().min(0).max(1).nullable().optional(),
  qualityReasons: array(string()).nullable().optional(),
});
export type AiEnrichment = z.infer<typeof AiEnrichmentSchema>;

/**
 * Full admin-facing ticket (a FeedbackSession). Org-scoped; only the backend
 * returns this after auth + org-scope enforcement.
 */
export const TicketSchema = object({
  id: string().min(1),
  organizationId: string().min(1),
  widgetInstanceId: string().min(1),
  status: TicketStatusSchema,
  channel: ChannelSchema,
  lang: string().nullable().optional(),
  pageUrl: string().nullable().optional(),
  pageTitle: string().nullable().optional(),
  userAgent: string().nullable().optional(),
  viewport: ViewportSchema.nullable().optional(),

  // Captured payloads.
  transcript: array(TranscriptTurnSchema).optional(),
  actions: array(UserActionSchema).optional(),
  consoleLogs: array(ConsoleLogSchema).optional(),
  errors: array(CapturedErrorSchema).optional(),

  // AI enrichment (flattened to mirror the columns).
  aiSummary: string().nullable().optional(),
  aiTitle: string().nullable().optional(),
  aiCategory: string().nullable().optional(),
  aiSentiment: SentimentSchema.nullable().optional(),
  qualityScore: number().min(0).max(1).nullable().optional(),
  qualityReasons: array(string()).nullable().optional(),

  clusterId: string().nullable().optional(),
  endUserEmail: string().nullable().optional(),
  emailOptIn: boolean(),
  spamScore: number().min(0).max(1),

  attachments: array(AttachmentSchema).optional(),

  createdAt: iso.datetime(),
  finalizedAt: iso.datetime().nullable().optional(),
});
export type Ticket = z.infer<typeof TicketSchema>;

/** Body for updating a ticket's status (admin). */
export const UpdateTicketSchema = object({
    status: TicketStatusSchema.optional(),
    aiTitle: string().max(512).optional(),
  })
  .refine((v) => v.status !== undefined || v.aiTitle !== undefined, {
    message: 'At least one field must be provided',
  });
export type UpdateTicket = z.infer<typeof UpdateTicketSchema>;

/** Query params for listing tickets (admin feed). */
export const ListTicketsQuerySchema = object({
  status: TicketStatusSchema.optional(),
  widgetInstanceId: string().optional(),
  clusterId: string().optional(),
  /** Cursor (ticket id) for keyset pagination. */
  cursor: string().optional(),
  limit: coerce.number().int().min(1).max(100).default(25),
});
export type ListTicketsQuery = z.infer<typeof ListTicketsQuerySchema>;

/** Paginated ticket list response. */
export const TicketListResponseSchema = object({
  items: array(TicketSchema),
  nextCursor: string().nullable(),
});
export type TicketListResponse = z.infer<typeof TicketListResponseSchema>;
